// ─────────────────────────────────────────
//  SHARE / DEPLOY / DOWNLOAD
// ─────────────────────────────────────────

import { S, sb, setLs } from './state.js';
import { showToast, detectCategory, trackSignal } from './utils.js';
import { getProjects } from './projects.js';
import { _generatedFiles, _generatedSpec, isFullStackMode } from './fullstack.js';

export function shareCode() {
  if (!S.code) { showToast('⚠ Nothing to share yet'); return; }
  // Stop the preview iframe to free up network for the deploy request
  const iframe = document.getElementById('preview-iframe');
  if (iframe) iframe.srcdoc = '<html><body style="background:#1d1c35"></body></html>';
  const modal = document.getElementById('deploy-modal');
  if (modal) modal.classList.add('open');
}

export function deployProject() {
  if (!S.session) { showToast('Please sign in first'); return; }
  if (!S.code) { showToast('⚠ Nothing to deploy'); return; }

  // Stop the preview iframe to free up browser connections
  const iframe = document.getElementById('preview-iframe');
  const savedSrcdoc = iframe?.srcdoc;
  if (iframe) iframe.srcdoc = '';

  const btn = document.getElementById('deploy-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Deploying...'; }

  // Refresh session to ensure token is valid
  sb.auth.getSession()
    .then(({ data }) => {
      const freshSession = data?.session;
      const authToken = freshSession?.access_token || S.session?.access_token;
      if (!authToken) {
        showToast('⚠ Session expired — please sign out and sign in again');
        if (iframe && savedSrcdoc) iframe.srcdoc = savedSrcdoc;
        if (btn) { btn.disabled = false; btn.textContent = 'Deploy →'; }
        return;
      }

      const nameEl = document.getElementById('deploy-name');
      const descEl = document.getElementById('deploy-desc');
      const galleryEl = document.getElementById('deploy-gallery');
      const name = nameEl?.value.trim() || 'My App';
      const description = descEl?.value.trim() || '';
      const inGallery = galleryEl?.checked || false;

      // Detect category for gallery
      const firstMsg = S.history.find(m => m.role === 'user')?.content || '';
      const category = detectCategory(firstMsg);

      fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authToken,
        },
        body: JSON.stringify({
          id: S.projectId || undefined,
          name,
          description,
          code: S.code,
          mode: S.mode,
          is_shared: true,
          in_gallery: inGallery,
          gallery_category: inGallery ? category : null,
          is_fullstack_preview: isFullStackMode(),
        }),
      })
        .then(res => res.json())
        .then(data => {
          if (data.project?.share_slug) {
            S.projectId = data.project.id;

            // Save remote ID to local project
            if (S.localProjectId) {
              const ps = getProjects();
              const local = ps.find(p => p.id === S.localProjectId);
              if (local) {
                local.remoteId = data.project.id;
                local.shareSlug = data.project.share_slug;
                setLs('vc_projects', JSON.stringify(ps));
              }
            }

            const liveUrl = location.origin + '/p/' + data.project.share_slug;
            const resultEl = document.getElementById('deploy-result');
            const urlEl = document.getElementById('deploy-url');
            if (resultEl) resultEl.style.display = 'block';
            if (urlEl) urlEl.value = liveUrl;
            trackSignal('deploy');
            showToast(inGallery ? '✓ Deployed & shared to gallery!' : '✓ Deployed!');
          } else {
            showToast('⚠ Deploy failed: ' + (data.error || 'Unknown error'));
          }
        })
        .catch(() => {
          showToast('⚠ Deploy failed — check your connection');
        })
        .finally(() => {
          if (btn) { btn.disabled = false; btn.textContent = 'Deploy →'; }
          if (iframe && savedSrcdoc) iframe.srcdoc = savedSrcdoc;
        });
    })
    .catch(() => {
      showToast('⚠ Session error — please sign out and sign in again');
      if (btn) { btn.disabled = false; btn.textContent = 'Deploy →'; }
      if (iframe && savedSrcdoc) iframe.srcdoc = savedSrcdoc;
    });
}

export function copyDeployUrl() {
  const urlEl = document.getElementById('deploy-url');
  const url = urlEl?.value;
  if (!url) return;
  navigator.clipboard.writeText(url)
    .then(() => showToast('✓ URL copied!'))
    .catch(() => prompt('Copy this URL:', url));
}

export function shareDeployUrl() {
  const urlEl = document.getElementById('deploy-url');
  const url = urlEl?.value;
  if (!url) return;
  if (navigator.share) {
    navigator.share({ title: 'My KasiCode App', text: 'Check out this app I built!', url });
  } else {
    copyDeployUrl();
  }
}

export function closeDeployModal(e) {
  const modal = document.getElementById('deploy-modal');
  if (!e || e.target === modal) {
    if (modal) modal.classList.remove('open');
  }
  const resultEl = document.getElementById('deploy-result');
  if (resultEl) resultEl.style.display = 'none';
  // Restore preview
  if (S.code) {
    const iframe = document.getElementById('preview-iframe');
    if (iframe) iframe.srcdoc = S.code;
  }
}

export function downloadCode() {
  if (!S.code) { showToast('⚠ Nothing to download'); return; }

  const ext = S.mode === 'react' ? 'jsx' : 'html';
  const filename = 'KasiCode-app.' + ext;
  const blob = new Blob([S.code], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast('✓ Downloaded ' + filename);
}

// ─── SUBDOMAIN PUBLISHING ────────────────────────────────

let _subdomainCheckTimeout = null;

export function initPublishUI() {
  const input = document.getElementById('publish-subdomain');
  if (!input) return;

  input.addEventListener('input', () => {
    clearTimeout(_subdomainCheckTimeout);
    const val = input.value.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/^-+/, '');
    input.value = val;

    const statusEl = document.getElementById('publish-status');
    if (!val || val.length < 3) {
      if (statusEl) { statusEl.textContent = val.length > 0 ? 'Minimum 3 characters' : ''; statusEl.style.color = 'var(--muted)'; }
      return;
    }

    if (statusEl) { statusEl.textContent = 'Checking...'; statusEl.style.color = 'var(--muted)'; }

    _subdomainCheckTimeout = setTimeout(() => {
      checkSubdomainAvailability(val);
    }, 500);
  });
}

function checkSubdomainAvailability(subdomain) {
  const statusEl = document.getElementById('publish-status');
  fetch('/api/projects/publish?subdomain=' + encodeURIComponent(subdomain))
    .then(res => res.json())
    .then(data => {
      if (!statusEl) return;
      if (data.available) {
        statusEl.textContent = '✓ Available!';
        statusEl.style.color = '#00d97e';
      } else {
        statusEl.textContent = '✕ ' + (data.reason || 'Already taken');
        statusEl.style.color = '#ff3b30';
      }
    })
    .catch(() => {
      if (statusEl) { statusEl.textContent = ''; }
    });
}

export function publishToSubdomain() {
  if (!S.session) { showToast('Please sign in first'); return; }
  if (!S.projectId) { showToast('⚠ Deploy your app first (click Deploy above)'); return; }

  const input = document.getElementById('publish-subdomain');
  const subdomain = input?.value?.toLowerCase().trim();

  if (!subdomain || subdomain.length < 3) {
    showToast('⚠ Enter a subdomain (at least 3 characters)');
    return;
  }

  const btn = document.getElementById('publish-btn');
  if (btn) { btn.disabled = true; btn.textContent = '🌐 Publishing...'; }

  sb.auth.getSession().then(({ data }) => {
    const authToken = data?.session?.access_token || S.session?.access_token;
    if (!authToken) {
      showToast('⚠ Session expired — please sign in again');
      if (btn) { btn.disabled = false; btn.textContent = '🌐 Publish to subdomain'; }
      return;
    }

    fetch('/api/projects/publish', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + authToken,
      },
      body: JSON.stringify({
        projectId: S.projectId,
        subdomain: subdomain,
      }),
    })
      .then(res => res.json().then(d => ({ ok: res.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) {
          showToast('⚠ ' + (data.error || 'Publish failed'));
          return;
        }

        // Success
        const resultEl = document.getElementById('publish-result');
        const urlEl = document.getElementById('publish-live-url');
        if (resultEl) resultEl.style.display = 'block';
        if (urlEl) {
          urlEl.href = data.url;
          urlEl.textContent = data.url;
        }
        showToast('✓ Published to ' + data.url);
        trackSignal('publish_subdomain');
      })
      .catch(() => {
        showToast('⚠ Publish failed — check your connection');
      })
      .finally(() => {
        if (btn) { btn.disabled = false; btn.textContent = '🌐 Publish to subdomain'; }
      });
  });
}

export function downloadFullStackZip() {
  if (!Object.keys(_generatedFiles).length) {
    showToast('⚠ No files to download');
    return;
  }

  showToast('📦 Preparing ZIP...');

  fetch('/api/fullstack/download', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      files: _generatedFiles,
      appName: _generatedSpec.appName || 'kasicode-app',
    }),
  })
    .then(res => res.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (_generatedSpec.appName || 'kasicode-app').toLowerCase().replace(/\s+/g, '-') + '.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('✓ Downloaded!');
    })
    .catch(() => {
      showToast('⚠ Download failed');
    });
}

export function pushToGitHub() {
  if (!Object.keys(_generatedFiles).length) {
    showToast('⚠ No files to push');
    return;
  }

  if (!S.session) {
    showToast('Please sign in first');
    return;
  }

  // Check for stored GitHub PAT first, then fall back to OAuth provider token
  const storedGithubToken = localStorage.getItem('vc_github_token');
  const providerToken = S.session.provider_token;

  if (!storedGithubToken && !providerToken) {
    // No token available — prompt to connect in settings or re-auth with GitHub
    showToast('⚠ Connect your GitHub account in Settings first');
    return;
  }

  const btn = document.querySelector('[onclick*="pushToGitHub"]');
  if (btn) { btn.disabled = true; btn.textContent = '🐙 Pushing to GitHub...'; }

  fetch('/api/github/push', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + S.session.access_token,
    },
    body: JSON.stringify({
      files: _generatedFiles,
      appName: _generatedSpec.appName || 'kasicode-app',
      description: _generatedSpec.description || 'Built with KasiCode AI',
      githubToken: storedGithubToken || null,
    }),
  })
    .then(res => res.json().then(data => ({ ok: res.ok, data })))
    .then(({ ok, data }) => {
      if (!ok) {
        if (data.code === 'GITHUB_NOT_CONNECTED') {
          showToast('⚠ GitHub token expired — reconnect in Settings');
          localStorage.removeItem('vc_github_token');
          localStorage.removeItem('vc_github_user');
          return;
        }
        throw new Error(data.error || 'Push failed');
      }

      // Success — show repo URL and Vercel import link
      const wrap = document.createElement('div');
      wrap.className = 'msg assistant';
      wrap.innerHTML = `
        <div class="msg-label">KasiCode AI</div>
        <div class="msg-bubble" style="background:rgba(36,41,47,.4);border-color:#444;">
          <div style="font-weight:800;font-size:15px;margin-bottom:8px;">🐙 Pushed to GitHub!</div>
          <div style="font-size:13px;color:var(--muted);margin-bottom:12px;">
            ${data.filesCount} files pushed to <strong>${data.username}/${data.repoName}</strong>
          </div>
          <div style="display:flex;flex-direction:column;gap:8px;">
            <a href="${data.repoUrl}" target="_blank" style="padding:10px 14px;background:#24292f;border:1px solid #444;border-radius:8px;color:#fff;font-family:var(--mono);font-size:12px;text-decoration:none;display:flex;align-items:center;gap:6px;">
              🐙 View on GitHub →
            </a>
            <a href="${data.vercelImportUrl}" target="_blank" style="padding:10px 14px;background:#000;border:1px solid #333;border-radius:8px;color:#fff;font-family:var(--mono);font-size:12px;text-decoration:none;display:flex;align-items:center;gap:6px;">
              ▲ Import to Vercel & Deploy →
            </a>
          </div>
          <div style="font-size:11px;color:var(--muted2);margin-top:10px;line-height:1.5;">
            On Vercel: add your Supabase env vars, then deploy. Your app will be live in ~2 minutes.
          </div>
        </div>`;
      const msgs = document.getElementById('messages');
      if (msgs) {
        msgs.appendChild(wrap);
        msgs.scrollTop = 99999;
      }
      showToast('✓ Pushed to GitHub!');

      // Auto-run migrations if Supabase is connected
      runMigrationsIfConnected();
    })
    .catch(err => {
      showToast('⚠ GitHub push failed: ' + err.message);
    })
    .finally(() => {
      if (btn) { btn.disabled = false; btn.textContent = '🐙 Push to GitHub → Deploy on Vercel'; }
    });
}

// ── Run SQL migrations against connected Supabase ──
function runMigrationsIfConnected() {
  const supabaseUrl = localStorage.getItem('vc_supabase_url');
  const supabaseKey = localStorage.getItem('vc_supabase_key');
  if (!supabaseUrl || !supabaseKey) return;

  // Find SQL files in the generated project
  const sqlFiles = Object.entries(_generatedFiles)
    .filter(([path]) => path.endsWith('.sql'))
    .sort(([a], [b]) => a.localeCompare(b));

  if (!sqlFiles.length) return;

  // Combine all SQL into one migration
  const allSql = sqlFiles.map(([path, content]) => `-- File: ${path}\n${content}`).join('\n\n');

  showToast('⚡ Running database migrations...');

  // Execute SQL via Supabase REST (rpc or raw SQL endpoint)
  fetch(supabaseUrl + '/rest/v1/rpc/exec_sql', {
    method: 'POST',
    headers: {
      'apikey': supabaseKey,
      'Authorization': 'Bearer ' + supabaseKey,
      'Content-Type': 'application/json',
      'Prefer': 'return=minimal',
    },
    body: JSON.stringify({ query: allSql }),
  })
    .then(res => {
      if (res.ok || res.status === 204) {
        showMigrationSuccess(sqlFiles.length);
        return;
      }
      // Fallback: try the pg_query approach (Supabase SQL Editor API)
      return runMigrationViaQuery(supabaseUrl, supabaseKey, allSql, sqlFiles.length);
    })
    .catch(() => {
      // Fallback approach
      runMigrationViaQuery(supabaseUrl, supabaseKey, allSql, sqlFiles.length);
    });
}

function runMigrationViaQuery(supabaseUrl, supabaseKey, sql, fileCount) {
  // Use the Supabase Management API or direct pg endpoint
  // The /rest/v1/ endpoint doesn't support raw SQL, so we use the query endpoint
  fetch(supabaseUrl + '/rest/v1/', {
    method: 'POST',
    headers: {
      'apikey': supabaseKey,
      'Authorization': 'Bearer ' + supabaseKey,
      'Content-Type': 'application/json',
      'Prefer': 'return=minimal',
    },
    body: JSON.stringify({ query: sql }),
  })
    .then(res => {
      if (res.ok || res.status === 204 || res.status === 200) {
        showMigrationSuccess(fileCount);
      } else {
        showMigrationManual();
      }
    })
    .catch(() => {
      showMigrationManual();
    });
}

function showMigrationSuccess(fileCount) {
  const wrap = document.createElement('div');
  wrap.className = 'msg assistant';
  wrap.innerHTML = `
    <div class="msg-label">KasiCode AI</div>
    <div class="msg-bubble" style="background:rgba(0,217,126,.06);border-color:rgba(0,217,126,.3);">
      <div style="font-weight:700;margin-bottom:4px;">⚡ Database migrations applied!</div>
      <div style="font-size:13px;color:var(--muted);">
        ${fileCount} SQL file${fileCount > 1 ? 's' : ''} executed on your connected Supabase project. Tables and policies are ready.
      </div>
    </div>`;
  const msgs = document.getElementById('messages');
  if (msgs) { msgs.appendChild(wrap); msgs.scrollTop = 99999; }
}

function showMigrationManual() {
  const wrap = document.createElement('div');
  wrap.className = 'msg assistant';
  wrap.innerHTML = `
    <div class="msg-label">KasiCode AI</div>
    <div class="msg-bubble" style="background:rgba(255,107,53,.06);border-color:rgba(255,107,53,.3);">
      <div style="font-weight:700;margin-bottom:4px;">📋 Run migrations manually</div>
      <div style="font-size:13px;color:var(--muted);line-height:1.6;">
        Could not auto-run SQL on your Supabase project. This usually means the <code>exec_sql</code> function isn't set up yet.<br><br>
        <strong>Quick fix:</strong> Copy the SQL from your project's migration files and paste it into your
        <a href="https://supabase.com/dashboard/project/_/sql" target="_blank" style="color:var(--accent);">Supabase SQL Editor</a>.
      </div>
    </div>`;
  const msgs = document.getElementById('messages');
  if (msgs) { msgs.appendChild(wrap); msgs.scrollTop = 99999; }
}
